import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import { Clear, TuneRounded } from '@mui/icons-material';

import usePaginatedTrainingSets from '../hooks/usePaginatedTrainingSets';
import useSearchParams from '../hooks/useSearchParams';
import { useDebouncedSearchParam } from '../hooks/useDebouncedSearchParam';
import { ITrainingSet } from '../../types/my-types';
import { TS_LIMIT } from '../../constants';
import { UploadContextProvider } from '../upload/UploadContext';
import TrainingSetCreate from './TrainingSetCreate';
import TrainingSetList from './TrainingSetList';
import TrainingSetFiltersPopover, {
  FilterValues,
  emptyFilters,
} from './TrainingSetFiltersPopover';
import { filterInputSx, dropdownChipSx, activeFilterPillSx } from '../common/filterStyles';

/* ── Popover filter param keys ── */
const POPOVER_FILTER_KEYS = Object.keys(emptyFilters) as (keyof FilterValues)[];

const FILTER_LABELS: Record<string, string> = {
  trained: 'Trained',
  certified: 'Certified',
  created_at: 'Created',
  updated_at: 'Updated',
  training_set_id: 'TS ID',
  document: 'Document',
  field: 'Field',
  training_revision: 'Revision',
  vendor_id: 'Vendor ID',
  vendor_name: 'Vendor',
};

function TrainingSetTableWrapper() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(search);

  const perPage = Number(searchParams.get('perPage')) || TS_LIMIT;
  const page = Number(searchParams.get('page')) || 0;
  const labelContains = searchParams.get('label__contains') || '';
  const createdAt = searchParams.get('created_at') || '';
  const updatedAt = searchParams.get('updated_at') || '';
  const trainingSetId = searchParams.get('training_set_id') || '';
  const trained = searchParams.get('trained') || '';
  const certified = searchParams.get('certified') || '';
  const document = searchParams.get('document') || '';
  const field = searchParams.get('field') || '';
  const trainingRevision = searchParams.get('training_revision') || '';
  const vendorId = searchParams.get('vendor_id') || '';
  const vendorName = searchParams.get('vendor_name') || '';

  const { setSearchParam, removeSearchParamSingle } = useSearchParams();

  const { data, isLoading, isFetching } = usePaginatedTrainingSets({
    limit: perPage,
    defaultPage: page,
    labelContains,
    createdAt,
    updatedAt,
    trainingSetId,
    trained,
    certified,
    document,
    field,
    trainingRevision,
    vendorId,
    vendorName,
  });

  const trainingSets: ITrainingSet[] = data?.results || [];
  const count: number = data?.count || 0;

  /* ── Debounced label input ── */
  const { localValue: localLabel, setLocalValue: setLocalLabel, debouncedSet: debouncedSetLabel, clear: clearLabel } = useDebouncedSearchParam('label__contains', labelContains);

  /* ── Filters popover ── */
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>(null);

  const currentFilterValues: FilterValues = {
    trained,
    certified,
    created_at: createdAt,
    updated_at: updatedAt,
    document,
    field,
    training_revision: trainingRevision,
    vendor_id: vendorId,
    vendor_name: vendorName,
  };

  const handleApplyFilters = (values: FilterValues) => {
    const params = new URLSearchParams(search);
    params.set('page', '0');
    POPOVER_FILTER_KEYS.forEach((key) => {
      const val = values[key];
      if (val) {
        params.set(key, val);
      } else {
        params.delete(key);
      }
    });
    navigate({ search: params.toString() });
    setPopoverAnchorEl(null);
  };

  /* ── TS ID input (applies on Enter/blur) ── */
  const [localTsId, setLocalTsId] = useState(trainingSetId);
  useEffect(() => setLocalTsId(trainingSetId), [trainingSetId]);
  const applyTsId = () => {
    if (localTsId.trim()) {
      setSearchParam('training_set_id', localTsId.trim());
    } else {
      removeSearchParamSingle('training_set_id');
    }
  };
  const clearTsId = () => {
    setLocalTsId('');
    removeSearchParamSingle('training_set_id');
  };

  const popoverFiltersCount = POPOVER_FILTER_KEYS.filter((k) => searchParams.get(k)).length;
  const totalActiveFilters = popoverFiltersCount + (labelContains ? 1 : 0) + (trainingSetId ? 1 : 0);

  return (
    <Box sx={{ backgroundColor: '#F5F6FA', padding: '20px 40px' }}>
      {/* ── Row 1: Title bar ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Training Sets
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#65BBA9', fontWeight: 600 }}
          >
            {isLoading || isFetching ? '...' : (
              <>(<span data-cy="span-ts-count">{count.toLocaleString()}</span>)</>
            )}
          </Typography>
        </Box>

        <UploadContextProvider>
          <TrainingSetCreate />
        </UploadContextProvider>
      </Box>

      {/* ── Row 2: Filter controls — right-aligned ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: totalActiveFilters > 0 ? 1 : 1.5,
          justifyContent: 'flex-end',
        }}
      >
        <TextField
          size="small"
          placeholder="TS ID..."
          variant="outlined"
          autoComplete="off"
          value={localTsId}
          onChange={(e) => setLocalTsId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applyTsId()}
          onBlur={applyTsId}
          data-cy="filter-ts-id"
          sx={{ width: 120, ...filterInputSx }}
          InputProps={{
            endAdornment: localTsId && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={clearTsId}
                  edge="end"
                  sx={{ mr: -1, p: 0.25 }}
                >
                  <Clear sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          size="small"
          placeholder="Search by label..."
          variant="outlined"
          autoComplete="off"
          value={localLabel}
          onChange={(e) => {
            setLocalLabel(e.target.value);
            debouncedSetLabel(e.target.value);
          }}
          data-cy="text-ts-search-input"
          sx={{ width: 180, ...filterInputSx }}
          InputProps={{
            endAdornment: localLabel && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => clearLabel()}
                  edge="end"
                  sx={{ mr: -1, p: 0.25 }}
                >
                  <Clear sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Chip
          icon={<TuneRounded sx={{ fontSize: '16px !important' }} />}
          label={popoverFiltersCount > 0 ? `Filters (${popoverFiltersCount})` : 'Filters'}
          onClick={(e) => setPopoverAnchorEl(e.currentTarget)}
          data-cy="btn-more-filters"
          sx={{
            ...dropdownChipSx,
            ...(popoverFiltersCount > 0 && {
              borderColor: '#1D9AAA',
              backgroundColor: 'rgba(29,154,170,0.06)',
            }),
          }}
        />

        <TrainingSetFiltersPopover
          anchorEl={popoverAnchorEl}
          currentValues={currentFilterValues}
          onClose={() => setPopoverAnchorEl(null)}
          onApply={handleApplyFilters}
        />
      </Box>

      {/* ── Row 3: Active filter pills — right-aligned ── */}
      {totalActiveFilters > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {/* Training Set ID pill */}
          {trainingSetId && (
            <Chip
              label={`TS ID: ${trainingSetId}`}
              onDelete={() => clearTsId()}
              data-cy="pill-training_set_id"
              sx={activeFilterPillSx}
            />
          )}
          {labelContains && (
            <Chip
              label={`Label: ${labelContains}`}
              onDelete={() => clearLabel()}
              data-cy="pill-label"
              sx={activeFilterPillSx}
            />
          )}
          {POPOVER_FILTER_KEYS.map((key) => {
            const value = searchParams.get(key);
            if (!value) return null;
            return (
              <Chip
                key={key}
                label={`${FILTER_LABELS[key]}: ${value}`}
                onDelete={() => removeSearchParamSingle(key)}
                data-cy={`pill-${key}`}
                sx={activeFilterPillSx}
              />
            );
          })}
        </Box>
      )}

      {/* ── Table ── */}
      <TrainingSetList
        trainingSets={trainingSets}
        count={count}
        page={page}
        perPage={perPage}
        nextPage={() => {
          const total = data ? Math.ceil(data.count / perPage) : 1;
          if (page < total - 1) setSearchParam('page', page + 1);
        }}
        previousPage={() => {
          if (page > 0) setSearchParam('page', page - 1);
        }}
        isLoading={isLoading}
        isFetching={isFetching}
      />
    </Box>
  );
}

export default TrainingSetTableWrapper;
